class WorkStatsAnimation {
    constructor(section) {
        this.section = section;
        this.observer = null;
        this.hasAnimated = false;
    }

    init() {
        this.setupScrollAnimation();
    }

    setupScrollAnimation() {
        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.3
        };

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !this.hasAnimated) {
                    this.hasAnimated = true;
                    this.animateNumbers();
                    this.observer.disconnect();
                }
            });
        }, observerOptions);

        this.observer.observe(this.section);
    }

    animateNumbers() {
        const statItems = this.section.querySelectorAll('.work-stats__stat-item');

        statItems.forEach((statItem) => {
            const numberElement = statItem.querySelector('.work-stats__stat-number');
            if (!numberElement) return;

            const targetValue = numberElement.dataset.targetValue;
            if (!targetValue) return;

            // Check if it's the special "$95M" format
            const isSpecialFormat = statItem.classList.contains('work-stats__stat-item--special');

            if (isSpecialFormat) {
                this.animateSpecialFormat(numberElement, targetValue);
            } else {
                this.animateRegularNumber(numberElement, targetValue);
            }
        });
    }

    animateRegularNumber(element, targetValue) {
        const targetNumber = parseFloat(targetValue);
        if (isNaN(targetNumber)) return;

        const decimalMatch = targetValue.match(/\.(\d+)/);
        const decimalPlaces = decimalMatch ? decimalMatch[1].length : 0;

        const duration = 2000;
        const startTime = performance.now();
        const startValue = 0;

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const rawValue = startValue + (targetNumber - startValue) * easeOutQuart;
            const currentValue = decimalPlaces > 0 ? rawValue.toFixed(decimalPlaces) : Math.floor(rawValue);

            const mainNumberSpan = element.querySelector('.work-stats__stat-main-number');
            if (mainNumberSpan) {
                mainNumberSpan.textContent = currentValue;
            } else {
                const textNodes = Array.from(element.childNodes).filter(node => node.nodeType === Node.TEXT_NODE);
                if (textNodes.length > 0) {
                    textNodes[0].textContent = currentValue;
                } else {
                    const existingContent = element.innerHTML;
                    const suffixMatch = existingContent.match(/([^0-9.]+)$/);
                    const suffix = suffixMatch ? suffixMatch[1] : '';
                    element.innerHTML = currentValue + suffix;
                }
            }

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                const mainNumberSpan = element.querySelector('.work-stats__stat-main-number');
                if (mainNumberSpan) {
                    mainNumberSpan.textContent = targetNumber.toFixed(decimalPlaces);
                }
            }
        };

        requestAnimationFrame(animate);
    }

    animateSpecialFormat(element, targetValue) {
        const match = targetValue.match(/\$?([\d.]+)/);
        if (!match) return;

        const targetNumber = parseFloat(match[1]);
        if (isNaN(targetNumber)) return;

        const decimalMatch = match[1].match(/\.(\d+)/);
        const decimalPlaces = decimalMatch ? decimalMatch[1].length : 0;

        const mainNumberSpan = element.querySelector('.work-stats__stat-main-number');
        if (!mainNumberSpan) return;

        const duration = 2000;
        const startTime = performance.now();
        const startValue = 0;

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const rawValue = startValue + (targetNumber - startValue) * easeOutQuart;
            const currentValue = decimalPlaces > 0 ? rawValue.toFixed(decimalPlaces) : Math.floor(rawValue);

            mainNumberSpan.textContent = currentValue;

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                mainNumberSpan.textContent = targetNumber.toFixed(decimalPlaces);
            }
        };

        requestAnimationFrame(animate);
    }
}

// Initialize animations for all work-stats sections on the page
function initWorkStatsAnimations() {
    const workStatsSections = document.querySelectorAll('.work-stats');

    workStatsSections.forEach((section) => {
        // Skip if already initialized
        if (section.dataset.animationInitialized) return;
        section.dataset.animationInitialized = 'true';

        const animation = new WorkStatsAnimation(section);
        animation.init();
    });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWorkStatsAnimations);
} else {
    initWorkStatsAnimations();
}

// Also initialize for dynamically loaded sections (Shopify theme editor)
if (typeof document.addEventListener !== 'undefined') {
    document.addEventListener('shopify:section:load', (event) => {
        const section = event.detail?.section;
        if (section && section.classList.contains('work-stats')) {
            if (!section.dataset.animationInitialized) {
                section.dataset.animationInitialized = 'true';
                const animation = new WorkStatsAnimation(section);
                animation.init();
            }
        }
    });
}